import React, { useContext } from 'react';
import styled from 'styled-components';
import { MenuProps as AntdMenuProps } from 'antd/lib/menu';
import { AuthContext } from 'contexts';
import {
  TemplateRoles as TemplateScope,
  TemplateUsage as TemplateType,
} from '@bb/domain/dist/types/Template';
import { Dropdown, Menu, MenuItem, Icon, theme } from 'components';
import { kebabCase } from 'lodash';

const SCOPE_LABELS = {
  GLOBAL: 'Global',
  BRANCH: 'My Branch',
  TEAM: 'My Team',
  USER: 'My Profile',
};

interface MenuItem {
  label: string;
  value: TemplateScope;
}

export interface ScopeDropdownProps {
  value: TemplateScope;
  open: boolean;
  setOpen: (open: boolean) => void;
  onChange: (value: TemplateScope) => void;
  disabled?: boolean;
  templateType: TemplateType;
}

const ScopeDropdown = (props: ScopeDropdownProps) => {
  const { value, open, setOpen, onChange, disabled, templateType } = props;
  const { auth } = useContext(AuthContext);

  const menuItems: MenuItem[] = [];
  if (auth.roles[auth.role] >= auth.roles.GLOBAL_ADMIN) {
    menuItems.push({ label: 'Global', value: 'GLOBAL' });
  }
  if (auth.roles[auth.role] >= auth.roles.BRANCH_ADMIN) {
    menuItems.push({ label: 'My Branch', value: 'BRANCH' });
  }
  if (auth.team || auth.teams.length) {
    menuItems.push({ label: 'My Team', value: 'TEAM' });
  }
  if (auth.isLicensed) {
    menuItems.push({ label: 'My Profile', value: 'USER' });
  }

  const onSelect: AntdMenuProps['onClick'] = ({ key }) => {
    onChange(menuItems[Number(key)].value);
    setOpen(false);
  };

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={visible => setOpen(visible)}
      dropdownRender={() => (
        <Menu onClick={onSelect}>
          {menuItems.map(({ label }: any, idx: number) => (
            <MenuItem
              key={idx}
              data-cy={`template-select-apply-${kebabCase(label)}`}
            >
              {label}
            </MenuItem>
          ))}
        </Menu>
      )}
      overlayStyle={{
        minWidth: '12em',
      }}
      disabled={disabled}
    >
      <ScopeDropdownButton disabled={disabled} data-cy="template-select-apply">
        <ScopeCurrentValueLabel disabled={disabled}>
          {menuItems.find(item => item.value === value)?.label ||
            SCOPE_LABELS[value]}
        </ScopeCurrentValueLabel>
        <Icon flipped={open} type="arrowDownThick" width={8} />
      </ScopeDropdownButton>
    </Dropdown>
  );
};

export default ScopeDropdown;

// TODO: Make this a button for consistent hover behaviour
const ScopeDropdownButton = styled.div<{ disabled?: boolean }>`
  background: ${theme.gray3};
  border-radius: 4px;
  font-size: 13px;
  line-height: 24px;
  min-width: 12em;
  padding: 3px 12px;

  display: flex;
  flex-flow: row nowrap;
  align-items: baseline;

  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const ScopeCurrentValueLabel = styled.span<{ disabled?: boolean }>`
  flex: 1;
  ${({ disabled }) => (disabled ? 'color: rgba(0, 0, 0, 0.25);' : '')}
`;
